import { createContext, ReactNode, useContext, useRef } from "react";
import {
    useInvalidateOrRemove,
    useSetOrInvalidate
} from "./invalidationHelpers";
import { AggregationRequest } from "@/backend/aggregation/aggregationRequestTypes";
import {
    Assessment,
    AssessmentTeamData,
    Question
} from "@/backend/assessment/assessmentTypes";
import { AssessmentType } from "@/backend/assessmentType/assessmentTypeTypes";
import { AssessmentTypeVersion } from "@/backend/assessmentType/versionTypes";
import { EvaluationRequest } from "@/backend/evaluation/evaluationRequestTypes";
import { Firm } from "@/backend/firm/firmTypes";
import { Team } from "@/backend/firm/teamTypes";
import { PrepopulationRequest } from "@/backend/prepopulation/prepopulationRequestTypes";
import { User } from "@/backend/user/userTypes";
import { UTBTypeDefinition } from "@/backend/utbType/utbTypeTypes";
import {
    FirmAssessmentType,
    FirmAssessmentTypeIds
} from "@/frontend/firm/api/firmAssessmentTypeApi";
import { FirmUser, FirmUserIds } from "@/frontend/firm/api/firmUserApi";
import {
    FirmUTBType,
    FirmUTBTypeIds
} from "@/frontend/firm/api/firmUTBTypeApi";

interface AssessmentTypeVersionIds {
    assessmentTypeId: string;
    id: string;
}

interface AssessmentIds {
    id: string;
    firmId: string;
    assessmentTypeId: string;
}

interface AssessmentTeamDataIds {
    firmId: string;
    assessmentId: string;
    teamId: string;
}

interface QuestionIds {
    firmId: string;
    assessmentId: string;
    teamId: string;
    key: string;
}

interface PrepopulationRequestIds {
    firmId: string;
    assessmentId: string;
    id: string;
}

interface EvaluationRequestIds {
    firmId: string;
    assessmentId: string;
    id: string;
}

interface AggregationRequestIds {
    firmId: string;
    assessmentId: string;
    id: string;
}

interface TeamIds {
    id: string;
    firmId: string;
}

// ------------------------

type QueryKey = (string | number | undefined)[];

type CollectionIdMap = {
    [key in keyof TypeMap]: (value: TypeMap[key][1]) => string | undefined;
};

type DeleteIdMap = {
    [key in keyof TypeMap]: (value: TypeMap[key][2]) => string | undefined;
};

type CallbackCollection = Partial<
    Record<keyof TypeMap, ((object: unknown) => Promise<void> | undefined)[]>
>;

interface BasicId {
    id: string;
}

// [0, 1, 2] = [propagate object, collection object, propagate delete id]
interface TypeMap {
    user: [User, User, BasicId];
    firm: [Firm, Firm, BasicId];
    firmUser: [FirmUser, Firm | User, FirmUserIds];
    team: [Team, Team, TeamIds];
    assessmentType: [AssessmentType, AssessmentType, BasicId];
    assessmentTypeVersion: [
        AssessmentTypeVersion,
        AssessmentTypeVersion,
        AssessmentTypeVersionIds
    ];
    firmAssessmentType: [
        FirmAssessmentType,
        Firm | AssessmentType,
        FirmAssessmentTypeIds
    ];
    assessment: [Assessment, Assessment, AssessmentIds];
    assessmentTeamData: [
        AssessmentTeamData,
        AssessmentTeamData,
        AssessmentTeamDataIds
    ];
    question: [Question, Question, QuestionIds];
    prepopulationRequest: [
        PrepopulationRequest,
        PrepopulationRequest,
        PrepopulationRequestIds
    ];
    evaluationRequest: [
        EvaluationRequest,
        EvaluationRequest,
        EvaluationRequestIds
    ];
    aggregationRequest: [
        AggregationRequest,
        AggregationRequest,
        AggregationRequestIds
    ];
    utbType: [UTBTypeDefinition, UTBTypeDefinition, BasicId];
    firmUTBType: [FirmUTBType, Firm | UTBTypeDefinition, FirmUTBTypeIds];
}

const collectionIdMap: CollectionIdMap = {
    user: x => x.id,
    firm: x => x.id,
    firmUser: x => x.id,
    team: x => x.id,
    assessmentType: x => x.id,
    assessmentTypeVersion: x => x.id,
    firmAssessmentType: x => x.id,
    assessment: x => x.id,
    assessmentTeamData: x => x.teamId,
    question: x => x.key,
    prepopulationRequest: x => x.id,
    evaluationRequest: x => x.id,
    aggregationRequest: x => x.id,
    utbType: x => x.id,
    firmUTBType: x => x.id
};

const deleteIdMap: DeleteIdMap = {
    user: x => x.id,
    firm: x => x.id,
    firmUser: _ => {
        throw new Error("firmUser delete id not implemented");
    },
    team: x => x.id,
    assessmentType: x => x.id,
    assessmentTypeVersion: x => x.id,
    firmAssessmentType: _ => {
        throw new Error("firmAssessmentType delete id not implemented");
    },
    assessment: x => x.id,
    assessmentTeamData: x => x.teamId,
    question: x => x.key,
    prepopulationRequest: x => x.id,
    evaluationRequest: x => x.id,
    aggregationRequest: x => x.id,
    utbType: x => x.id,
    firmUTBType: _ => {
        throw new Error("firmUTBType delete id not implemented");
    }
};

const PropagateContext = createContext<ReturnType<typeof usePropagateState>>({
    alreadySetCheck: () => true,
    onUpsertMerge: () => undefined,
    onUpsertMergeInCollection: () => undefined,
    onUpsertInvalidate: () => undefined,
    onUpsertAddToCollection: () => undefined,
    onDeleteRemoveFromCollection: () => undefined,
    onDeleteInvalidate: () => undefined,
    onRemoteUpsertInvalidate: () => undefined,
    onCollectionDefaults: () => undefined,
    onSingleDefaults: () => undefined,
    onAnyInvalidate: () => undefined,
    propagateUpsert: () => undefined,
    propagateDelete: () => undefined,
    propagateRemoteUpsert: () => undefined
});

const usePropagate = () => useContext(PropagateContext);

function setObjectCallback<T extends keyof TypeMap>(
    collection: CallbackCollection,
    type: T,
    callback: (object: TypeMap[T][0]) => Promise<void> | undefined
): void {
    if (!collection[type]) {
        collection[type] = [];
    }
    collection[type].push(
        callback as unknown as (object: unknown) => Promise<void>
    );
}

function setIdCallback<T extends keyof TypeMap>(
    collection: CallbackCollection,
    type: T,
    callback: (id: TypeMap[T][2]) => Promise<void> | undefined
): void {
    if (!collection[type]) {
        collection[type] = [];
    }
    collection[type].push(
        callback as unknown as (id: unknown) => Promise<void>
    );
}

function getCollectionId<T extends keyof TypeMap>(
    type: T,
    object: TypeMap[T][1]
): string | undefined {
    return collectionIdMap[type](object);
}

function getDeleteId<T extends keyof TypeMap>(
    type: T,
    id: TypeMap[T][2]
): string | undefined {
    return deleteIdMap[type](id);
}

function executeCallbackCollection(
    collection: CallbackCollection,
    type: keyof TypeMap,
    object: unknown
): Promise<unknown> | undefined {
    if (!collection[type]) {
        return undefined;
    }

    const promises: Promise<void>[] = [];

    for (const callback of collection[type]) {
        const result = callback(object);
        if (result) {
            promises.push(result);
        }
    }

    if (promises.length > 0) {
        return Promise.all(promises);
    }

    return undefined;
}

function usePropagateState() {
    const setOrInvalidate = useSetOrInvalidate();
    const invalidateOrRemove = useInvalidateOrRemove();

    const alreadySet = useRef<Record<string, true>>({});
    const upsertCallbacks = useRef<CallbackCollection>({});
    const deleteCallbacks = useRef<CallbackCollection>({});
    const remoteUpsertCallbacks = useRef<CallbackCollection>({});

    const alreadySetCheck = (key: string) => {
        const isSet = key in alreadySet.current;
        alreadySet.current[key] = true;
        return isSet;
    };

    function onUpsertMerge<T extends keyof TypeMap>(
        type: T,
        queryKey: QueryKey | ((updatedValue: TypeMap[T][0]) => QueryKey)
    ): void {
        setObjectCallback(upsertCallbacks.current, type, object => {
            const myQueryKey =
                typeof queryKey === "function" ? queryKey(object) : queryKey;

            setOrInvalidate<TypeMap[T][1]>(myQueryKey, old =>
                old === object
                    ? old
                    : {
                          ...old,
                          ...object
                      }
            );
            return undefined;
        });
    }

    function onUpsertMergeInCollection<T extends keyof TypeMap>(
        type: T,
        queryKey: QueryKey | ((updatedValue: TypeMap[T][0]) => QueryKey)
    ): void {
        setObjectCallback(upsertCallbacks.current, type, object => {
            const myQueryKey =
                typeof queryKey === "function" ? queryKey(object) : queryKey;
            const objectId = getCollectionId(type, object as TypeMap[T][1]);

            setOrInvalidate<TypeMap[T][1][]>(myQueryKey, old => {
                let hasChange = false;
                const replace = old.map(x => {
                    if (x !== object && getCollectionId(type, x) === objectId) {
                        hasChange = true;
                        return { ...x, ...object };
                    } else {
                        return x;
                    }
                });
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                return hasChange ? replace : old;
            });
            return undefined;
        });
    }

    function onUpsertInvalidate<T extends keyof TypeMap>(
        type: T,
        queryKey: QueryKey | ((updatedValue: TypeMap[T][0]) => QueryKey)
    ): void {
        setObjectCallback(upsertCallbacks.current, type, object => {
            const myQueryKey =
                typeof queryKey === "function" ? queryKey(object) : queryKey;

            return invalidateOrRemove(myQueryKey) as Promise<void>;
        });
    }

    function onUpsertAddToCollection<T extends keyof TypeMap>(
        type: T,
        queryKey: QueryKey | ((updatedValue: TypeMap[T][0]) => QueryKey),
        objectGetter?: (updatedValue: TypeMap[T][0]) => TypeMap[T][1]
    ): void {
        setObjectCallback(upsertCallbacks.current, type, object => {
            const myQueryKey =
                typeof queryKey === "function" ? queryKey(object) : queryKey;
            const resolvedObject = objectGetter
                ? objectGetter(object)
                : (object as TypeMap[T][1]);
            const objectId = getCollectionId(type, resolvedObject);

            setOrInvalidate<TypeMap[T][1][]>(myQueryKey, old => {
                const existing = old.find(
                    x => getCollectionId(type, x) === objectId
                );
                if (existing) {
                    return old;
                }
                return [...old, resolvedObject];
            });
            return undefined;
        });
    }

    function onDeleteRemoveFromCollection<T extends keyof TypeMap>(
        type: T,
        queryKey: QueryKey | ((updatedValue: TypeMap[T][2]) => QueryKey),
        idGetter?: (deletedId: TypeMap[T][2]) => string | undefined
    ): void {
        setIdCallback(deleteCallbacks.current, type, id => {
            const myQueryKey =
                typeof queryKey === "function" ? queryKey(id) : queryKey;
            const objectId = idGetter ? idGetter(id) : getDeleteId(type, id);

            setOrInvalidate<TypeMap[T][1][]>(myQueryKey, old => {
                let hasChange = false;
                const replace = old.filter(x => {
                    if (getCollectionId(type, x) === objectId) {
                        hasChange = true;
                        return false;
                    } else {
                        return true;
                    }
                });
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                return hasChange ? replace : old;
            });
            return undefined;
        });
    }

    function onDeleteInvalidate<T extends keyof TypeMap>(
        type: T,
        queryKey: QueryKey | ((updatedValue: TypeMap[T][2]) => QueryKey)
    ): void {
        setIdCallback(deleteCallbacks.current, type, id => {
            const myQueryKey =
                typeof queryKey === "function" ? queryKey(id) : queryKey;

            return invalidateOrRemove(myQueryKey) as Promise<void>;
        });
    }

    function onRemoteUpsertInvalidate<T extends keyof TypeMap>(
        type: T,
        queryKey:
            | QueryKey
            | ((updatedValue: Partial<TypeMap[T][2]>) => QueryKey)
    ): void {
        setIdCallback(remoteUpsertCallbacks.current, type, id => {
            const myQueryKey =
                typeof queryKey === "function" ? queryKey(id) : queryKey;

            // trim undefined values from end of query key
            const index = myQueryKey.findIndex(x => x === undefined);
            const trimmedQueryKey =
                index >= 0 ? myQueryKey.slice(0, index) : myQueryKey;

            return invalidateOrRemove(trimmedQueryKey) as Promise<void>;
        });
    }

    function onSingleDefaults<T extends keyof TypeMap>(
        type: T,
        queryKey:
            | QueryKey
            | ((
                  updatedValue: Partial<TypeMap[T][0] | TypeMap[T][2]>
              ) => QueryKey)
    ): void {
        onUpsertMerge(type, queryKey);
        onRemoteUpsertInvalidate(type, queryKey);
        onDeleteInvalidate(type, queryKey);
    }

    function onCollectionDefaults<T extends keyof TypeMap>(
        type: T,
        queryKey:
            | QueryKey
            | ((
                  updatedValue: Partial<TypeMap[T][0] | TypeMap[T][2]>
              ) => QueryKey)
    ): void {
        onUpsertMergeInCollection(type, queryKey);
        onUpsertAddToCollection(type, queryKey);
        onRemoteUpsertInvalidate(type, queryKey);
        onDeleteRemoveFromCollection(type, queryKey);
    }

    function onAnyInvalidate<T extends keyof TypeMap>(
        type: T,
        queryKey:
            | QueryKey
            | ((
                  updatedValue: Partial<TypeMap[T][0] | TypeMap[T][2]>
              ) => QueryKey)
    ): void {
        onUpsertInvalidate(type, queryKey);
        onRemoteUpsertInvalidate(type, queryKey);
        onDeleteInvalidate(type, queryKey);
    }

    function propagateUpsert<T extends keyof TypeMap>(
        type: T,
        object: TypeMap[T][0]
    ) {
        return executeCallbackCollection(upsertCallbacks.current, type, object);
    }

    function propagateDelete<T extends keyof TypeMap>(
        type: T,
        id: TypeMap[T][2]
    ) {
        return executeCallbackCollection(deleteCallbacks.current, type, id);
    }

    function propagateRemoteUpsert<T extends keyof TypeMap>(
        type: T,
        id: Partial<TypeMap[T][2]>
    ) {
        return executeCallbackCollection(
            remoteUpsertCallbacks.current,
            type,
            id
        );
    }

    return {
        alreadySetCheck,
        onUpsertMerge,
        onUpsertMergeInCollection,
        onUpsertInvalidate,
        onUpsertAddToCollection,
        onDeleteRemoveFromCollection,
        onDeleteInvalidate,
        onRemoteUpsertInvalidate,
        onCollectionDefaults,
        onSingleDefaults,
        onAnyInvalidate,
        propagateUpsert,
        propagateDelete,
        propagateRemoteUpsert
    };
}

export const PropagateProvider = ({ children }: { children: ReactNode }) => {
    const value = usePropagateState();
    return (
        <PropagateContext.Provider value={value}>
            {children}
        </PropagateContext.Provider>
    );
};

export function useOnPropagate(
    key: string,
    callback: (
        propagate: Omit<
            ReturnType<typeof usePropagateState>,
            | "alreadySetCheck"
            | "propagateUpsert"
            | "propagateDelete"
            | "propagateRemoteUpsert"
        >
    ) => void
) {
    const propagate = usePropagate();

    if (propagate.alreadySetCheck(key)) {
        return;
    }

    callback(propagate);
}

export function usePropagateUpsert() {
    const { propagateUpsert } = usePropagate();
    return propagateUpsert;
}

export function usePropagateDelete() {
    const { propagateDelete } = usePropagate();
    return propagateDelete;
}

export function usePropagateRemoteUpsert() {
    const { propagateRemoteUpsert } = usePropagate();
    return propagateRemoteUpsert;
}
